import React, { createContext, useState, useContext, useEffect } from 'react';
import { getToken, setToken as setTokenUtil, removeToken as removeTokenUtil } from '../utils/auth';
import { workspaceService } from '../services/workspaceService';
import { intercomService } from '../services/intercomService';
import { fetchMeAccess } from '../services/api/meAccess';
import { fetchMe } from '../services/api/me';
import posthog from 'posthog-js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authToken, setAuthToken] = useState(null);
    const [user, setUser] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [currentWorkspace, setCurrentWorkspace] = useState(null);
    // True once fetchWorkspaces() has settled at least once for the current
    // session. Route guards (RootRedirect, WorkspaceGate, PrivateLayoutRoute)
    // wait on this so a user with zero live workspaces sees the no-workspace
    // screen instead of being misread as "still loading" forever.
    const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
    // True when the most recent fetchWorkspaces() call itself failed (network
    // error, 5xx, etc.) — distinct from workspacesLoaded, which must stay
    // false on failure so guards don't misread a fetch error as "confirmed
    // zero workspaces" and show every user (including ones with workspaces)
    // the no-workspace screen.
    const [workspacesError, setWorkspacesError] = useState(false);
    const [intercomData, setIntercomData] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [access, setAccess] = useState({ features: [], permissions: {}, role: null, internal_admin: false });
    // True once /me/access has settled at least once. Route-level feature guards
    // wait on this so an entitled user isn't bounced off a plan-gated page during
    // the brief window after a hard refresh, before access.features has loaded.
    const [accessLoaded, setAccessLoaded] = useState(false);

    useEffect(() => {
        if (isInitialized) return;

        // OAuth-redirect race fix: if the URL carries a freshly-issued token
        // (?token=... &user_data=...), persist it BEFORE PrivateLayoutRoute can
        // see isAuthenticated=false and bounce us to /login. The legacy
        // TokenHandler in App.js still runs but only as a no-op fallback.
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('token');
            const urlUserData = urlParams.get('user_data');
            // Require user_data: this block is the OAuth-redirect race fix and
            // OAuth always sends ?token=<JWT>&user_data=... . The invite link
            // reuses ?token=<invitationToken> WITHOUT user_data — grabbing it
            // here logged the invitee in with a bogus token and broke the
            // accept-invite page. Gating on user_data ignores the invite token.
            if (urlToken && urlUserData && !localStorage.getItem('token')) {
                const cleanToken = urlToken.startsWith('Bearer ') ? urlToken.slice(7) : urlToken;
                setTokenUtil(cleanToken);
                if (urlUserData) {
                    try {
                        const parsed = JSON.parse(atob(urlUserData));
                        localStorage.setItem('userData', JSON.stringify(parsed));
                    } catch (_) { /* fall back to /api/v1/me below */ }
                }
            }
        } catch (_) { /* SSR or unusual env — skip */ }

        const token = getToken();
        const storedUserData = localStorage.getItem('userData');
        const storedWorkspace = localStorage.getItem('currentWorkspace');

        if (token) {
            setAuthToken(token);
            setIsAuthenticated(true);

            if (storedUserData) {
                try {
                    const userData = JSON.parse(storedUserData);
                    setUser(userData);
                } catch (error) {
                    console.error('Error parsing stored user data:', error);
                    localStorage.removeItem('userData');
                }
            }

            // Always fetch workspaces + access when a token is present, even if
            // localStorage has no userData (e.g. fresh incognito login). Dashboard
            // and other widgets gate on currentWorkspace being set.
            fetchWorkspaces();
            refreshAccess();

            if (storedWorkspace) {
                try {
                    const workspace = JSON.parse(storedWorkspace);
                    setCurrentWorkspace(workspace);
                    // fetchWorkspaces() will verify this workspace still exists and correct if needed
                } catch (error) {
                    console.error('Error parsing stored workspace:', error);
                    localStorage.removeItem('currentWorkspace');
                }
            }
        } else {
            setIsAuthenticated(false);
            setAuthToken(null);
            setUser(null);
            setWorkspaces([]);
            setWorkspacesLoaded(false);
            setWorkspacesError(false);
            setCurrentWorkspace(null);
            setAccessLoaded(true); // nothing to load; don't leave guards waiting
            localStorage.removeItem('currentWorkspace');
        }

        setIsInitialized(true);
    }, []);

    const fetchWorkspaces = async () => {
        setWorkspacesError(false);
        try {
            const data = await workspaceService.getWorkspaces();
            setWorkspaces(data);
            // Only mark "loaded" on a successful response — a failed request
            // must NOT be treated as "confirmed zero workspaces" (see catch).
            setWorkspacesLoaded(true);

            if (data.length === 0) {
                // No live workspaces left (all archived, or every membership
                // removed) — drop any stale current workspace so gates don't
                // keep pointing at a workspace that no longer exists.
                setCurrentWorkspace(null);
                localStorage.removeItem('currentWorkspace');
                return data;
            }

            // Restore the last-selected workspace. Priority:
            //   1. localStorage cache (this browser), if it still exists
            //   2. the workspace the backend marks as default — the last one
            //      selected on any device; survives fresh browsers, incognito,
            //      and cleared storage (where localStorage is empty)
            //   3. first workspace in the (now deterministically ordered) list
            const storedWorkspace = localStorage.getItem('currentWorkspace');
            let resolved = null;

            if (storedWorkspace) {
                try {
                    const stored = JSON.parse(storedWorkspace);
                    resolved = data.find(w => w.id === stored.id);
                } catch { /* ignore parse errors */ }
            }

            if (!resolved) {
                resolved = data.find(w => w.is_default) || null;
            }

            if (resolved) {
                // Found a remembered workspace — use it (includes refreshed role)
                setCurrentWorkspace(resolved);
                localStorage.setItem('currentWorkspace', JSON.stringify(resolved));
            } else {
                // Nothing remembered anywhere — pick the first (deterministically
                // ordered) workspace and persist it as the new default.
                if (storedWorkspace) localStorage.removeItem('currentWorkspace');
                switchWorkspace(data[0]);
            }

            return data;
        } catch (error) {
            console.error('Error fetching workspaces:', error);
            // Leave workspacesLoaded false — guards keep waiting/showing
            // WorkspacesLoadError instead of reading this as zero workspaces.
            setWorkspacesError(true);
            return null;
        }
    };

    const switchWorkspace = async (workspace) => {
        // Optimistically set the active workspace BEFORE the network round-trip.
        // The URL UUID is the source of truth for the active workspace, so the
        // WorkspaceGate calls this on every cross-workspace navigation/reload —
        // setting state first keeps the gate from blocking and guarantees pages
        // never fire API calls against a stale workspace id.
        setCurrentWorkspace(workspace);
        localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
        try {
            await workspaceService.setDefaultWorkspace(workspace.id);
            // Re-fetch /me/access so user.client_id reflects the new default
            // workspace. Without this, pages that read user.client_id keep
            // pointing at the previous workspace until the next reload.
            await refreshAccess();
            if (intercomData) {
                intercomService.updateCompany(intercomData, workspace.id);
            }
            posthog.capture('workspace_switched', { workspace_id: workspace.id });
        } catch (error) {
            console.error('Error switching workspace:', error);
        }
    };

    const createWorkspace = async ({ name, timezone, billingAccountId } = {}) => {
        const created = await workspaceService.createWorkspace({ name, timezone, billingAccountId });
        setWorkspaces((prev) => [...prev, created]);
        return created;
    };

    const refreshAccess = async () => {
        try {
            const { data } = await fetchMeAccess();
            setAccess(data);
            // Backend now returns user info in /me/access — fold it into the
            // user state so refreshes (without "remember me") still have name,
            // email, client_id, has_password populated.
            if (data?.user) {
                setUser((prev) => ({ ...(prev || {}), ...data.user }));
            }
        } catch (e) {
            // Silently keep prior access — backend issue shouldn't lock UI.
            console.warn('Failed to refresh /me/access:', e?.response?.status);
        } finally {
            // Mark settled either way so guards stop waiting. On a fetch error we
            // keep whatever access we had (fail-open on transient backend issues).
            setAccessLoaded(true);
        }
    };

    const setToken = (token) => {
        if (!token) {
            console.log('Warning: Attempting to set null/undefined token');
            return;
        }

        // Only update if the token is different
        if (token === authToken) {
            console.log('Token is the same, skipping update');
            return;
        }

        setTokenUtil(token);
        setAuthToken(token);
        setIsAuthenticated(true);
        setAccessLoaded(false); // re-wait for THIS session's access before gating
        setWorkspacesLoaded(false); // re-wait for THIS session's workspaces before gating
        setWorkspacesError(false);
        refreshAccess();
        fetchWorkspaces();

        // Verify token was set correctly
        const storedToken = getToken();
        if (storedToken !== token) {
            console.error('Token mismatch:', { stored: storedToken, received: token });
            removeToken();
            return;
        }
    };

    const removeToken = () => {
        posthog.capture('user_logged_out');
        posthog.reset();
        removeTokenUtil();
        setAuthToken(null);
        setIsAuthenticated(false);
        setUser(null);
        setWorkspaces([]);
        setCurrentWorkspace(null);
        setIntercomData(null);
        intercomService.shutdown();
        // Keep currentWorkspace in localStorage so it's restored on next login
    };

    // Add effect to sync workspace state with localStorage
    useEffect(() => {
        if (currentWorkspace) {
            localStorage.setItem('currentWorkspace', JSON.stringify(currentWorkspace));
        }
    }, [currentWorkspace]);

    // Once authenticated, fetch /me and wire up analytics + support tooling.
    // Runs on EVERY auth path — email login, Google OAuth redirect, post-signup
    // auto-login, and reload-with-token — so this is the single reliable place
    // to identify the user. (Login.js also identifies, but misses OAuth/reload.)
    useEffect(() => {
        if (!isAuthenticated) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await fetchMe();
                if (cancelled || !data) return;
                // PostHog: identify by EMAIL (stable, human-readable distinct_id)
                // so people are never keyed by raw id/uuid. Falls back to id only
                // if somehow there's no email.
                if (data.email || data.id) {
                    posthog.identify(data.email || String(data.id), { email: data.email, name: data.name });
                }
                // Intercom: boot the Messenger in secure mode when configured.
                if (data.intercom) {
                    setIntercomData(data.intercom);
                    intercomService.boot(data.intercom);
                }
            } catch (e) {
                // An analytics/Intercom hiccup must never block the app.
                console.warn('Failed to load /me for analytics/Intercom:', e?.response?.status);
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const value = {
        isAuthenticated,
        authToken,
        user,
        workspaces,
        currentWorkspace,
        workspacesLoaded,
        workspacesError,
        isInitialized,
        access,
        accessLoaded,
        setToken,
        removeToken,
        setUser,
        switchWorkspace,
        createWorkspace,
        fetchWorkspaces,
        refreshAccess
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export { AuthContext }; 